import { useState } from "react"
import { makeStyles } from "tss-react/mui"
import { useNavigate } from "react-router-dom"
import {
    Avatar,
    Box,
    Divider,
    Grid,
    IconButton,
    ListItemIcon,
    Menu,
    MenuItem,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material"
import { Logout, MeetingRoomRounded, Person, Place, Settings, HelpOutline, Campaign } from "@mui/icons-material"
import {
    getProfileRoute,
    getSettingsRoute,
    getGoPayNewsRoute,
    LOGIN_ROUTE,
    SERVICE_CENTER_REPORTS_PROFILE_ROUTE,
    USER_REPORTS_GOPAY_NEWS_ROUTE,
    USER_REPORTS_PROFILE_ROUTE,
} from "../../../constants/routes"
import { HelpDialog } from "../../components/dialogs/helpDialog/HelpDialog"
import { selectUserType } from "../../../modules/login/loginSelectors"
import { useAppLocalization } from "../../../modules/localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../modules/localization/components/AppLocalized"
import { useAppDispatch, useAppSelector } from "../../../modules/store/storeHooks"
import { logout } from "../../../modules/login/loginSlice"
import { selectUserInitials, selectUsername } from "../../../modules/login/loginSelectors"
import { selectEffectiveUserType } from "../../../modules/context/contextSelectors"
import { UserType } from "../../../modules/login/loginTypes"
import { SwitchMerchantDialog } from "../../../modules/merchant/admin/account/merchantAccount/components/SwitchMerchantDialog"
import { ChangeUserAccountDialog } from "../../../modules/profile/channgeUserAccount/components/ChangeUserAccountDialog"
import { MatchEnvironment } from "../../../modules/environment/components/MatchEnvironment"
import { EnvironmentType } from "../../../modules/environment/envTypes"
import { tailwindPalette } from "../../../modules/theme/tailwindPalette"
import { MatchActualUserType } from "../../../modules/accessControl/MatchActualUserType"
import { HideForCurrentUserType } from "../../../modules/accessControl/HideForCurrentUserType"
import { selectLoginUser } from "../../../modules/users/userSelectors"

type AvatarMenuProps = Readonly<{
    urlPrefix: string | undefined
}>

const useStyles = makeStyles()((theme) => ({
    text: {
        color: "#fffff",
    },
    userAvatar: {
        backgroundColor: tailwindPalette.slate[600],
        color: "white",
    },
    logoutIcon: {
        color: "#ef5350",
    },
    greyIcon: {
        color: theme.palette.tailwind.neutral[400],
    },
}))

export function AvatarMenu({ urlPrefix }: AvatarMenuProps) {
    const navigate = useNavigate()
    const { classes, cx } = useStyles()
    const { l10n } = useAppLocalization()
    const userInitials = useAppSelector(selectUserInitials)
    const currentUserType = useAppSelector(selectEffectiveUserType)
    const username = useAppSelector(selectUsername)
    const userDetails = useAppSelector(selectLoginUser)
    const dispatch = useAppDispatch()

    const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null)
    const [showSwiftMerchantModal, setShowSwiftMerchantModal] = useState(false)
    const [showChangeUserAccountModal, setShowChangeUserAccountModal] = useState(false)
    const [showHelpDialog, setShowHelpDialog] = useState(false)
    const actualUserType = useAppSelector(selectUserType)

    const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElUser(event.currentTarget)
    }

    const handleCloseUserMenu = () => {
        setAnchorElUser(null)
    }

    const navigateToProfilePage = () => {
        handleCloseUserMenu()
        if (currentUserType === UserType.USER) {
            navigate(`${USER_REPORTS_PROFILE_ROUTE}?tab=0`)
            return
        }

        if (currentUserType === UserType.SERVICE_CENTER) {
            navigate(`${SERVICE_CENTER_REPORTS_PROFILE_ROUTE}`)
            return
        }
        navigate(getProfileRoute(urlPrefix))
    }

    function navigateToSettingsPage() {
        handleCloseUserMenu()
        if (currentUserType === UserType.USER) {
            navigate(`${USER_REPORTS_PROFILE_ROUTE}?tab=1`)
            return
        }
        navigate(getSettingsRoute(urlPrefix))
    }

    function handleLogout() {
        dispatch(logout())

        // Not strictly needed since this route is access controlled
        navigate(LOGIN_ROUTE)
    }

    const handleOpenSwiftMerchantModal = () => {
        setShowSwiftMerchantModal(true)
        handleCloseUserMenu()
    }

    const handleOpenChangeUserAccountModal = () => {
        setShowChangeUserAccountModal(true)
        handleCloseUserMenu()
    }

    const handleOpenHelpDialog = () => {
        setShowHelpDialog(true)
        handleCloseUserMenu()
    }

    const handleNavigateToNews = () => {
        handleCloseUserMenu()
        if (actualUserType === UserType.USER) {
            navigate(USER_REPORTS_GOPAY_NEWS_ROUTE)
            return
        }
        navigate(getGoPayNewsRoute(urlPrefix))
    }

    return (
        <>
            <Tooltip title={l10n.getString("avatar-menu-tooltip")}>
                <IconButton onClick={handleOpenUserMenu} sx={{ p: 0, ml: 1 }}>
                    <Avatar className={classes.userAvatar}>
                        {userInitials?.toUpperCase() ?? userDetails?.displayInitials ?? "ME"}
                    </Avatar>
                </IconButton>
            </Tooltip>
            <Menu
                sx={{ mt: "45px" }}
                id="mobile-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                keepMounted
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
            >
                <Box sx={{ padding: ".7rem" }}>
                    <Grid container direction="row" justifyContent="flex-start" alignItems="center" minWidth="15rem">
                        <IconButton sx={{ mb: "0.2rem" }}>
                            <Avatar className={classes.userAvatar}>{userInitials?.toUpperCase() ?? "ME"}</Avatar>
                        </IconButton>
                        <Stack direction="column">
                            <HideForCurrentUserType userTypes={[UserType.USER]}>
                                <Typography variant="body1">{username}</Typography>
                            </HideForCurrentUserType>
                            <MatchActualUserType userTypes={[UserType.USER]}>
                                <Typography variant="body1">{userDetails?.displayName}</Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {username}
                                </Typography>
                            </MatchActualUserType>
                        </Stack>
                    </Grid>

                    <Divider sx={{ marginX: "1rem", marginBottom: "0.5rem" }} />
                    <MatchEnvironment
                        environments={[
                            EnvironmentType.Local,
                            EnvironmentType.Test,
                            EnvironmentType.Demo,
                            EnvironmentType.QA,
                        ]}
                    >
                        <MenuItem key="showHelp" onClick={handleOpenHelpDialog}>
                            <ListItemIcon>
                                <HelpOutline className={classes.greyIcon} fontSize="small" />
                            </ListItemIcon>
                            <LocalizedStrict id="avatar-menu-menu-item-help">
                                <Typography display="inline" textAlign="center">
                                    Help
                                </Typography>
                            </LocalizedStrict>
                        </MenuItem>
                    </MatchEnvironment>
                    <MenuItem key="aboutFacilitynet" onClick={handleNavigateToNews}>
                        <ListItemIcon>
                            <Campaign className={classes.greyIcon} fontSize="small" />
                        </ListItemIcon>
                        <LocalizedStrict id="avatar-menu-menu-item-news">
                            <Typography display="inline" textAlign="center">
                                Facilitynet news
                            </Typography>
                        </LocalizedStrict>
                    </MenuItem>

                    <MatchEnvironment
                        environments={[
                            EnvironmentType.Local,
                            EnvironmentType.Test,
                            EnvironmentType.Demo,
                            EnvironmentType.QA,
                        ]}
                    >
                        <Divider sx={{ marginX: "1rem", marginBottom: "0.5rem" }} />
                        <MenuItem key="changeUserAccount" onClick={handleOpenChangeUserAccountModal}>
                            <ListItemIcon>
                                <MeetingRoomRounded className={classes.greyIcon} fontSize="small" />
                            </ListItemIcon>
                            <LocalizedStrict id="avatar-menu-menu-item-change-user-account">
                                <Typography display="inline" textAlign="center">
                                    Change user account
                                </Typography>
                            </LocalizedStrict>
                        </MenuItem>

                        {currentUserType === UserType.MERCHANT && (
                            <>
                                <Divider sx={{ marginX: "1rem", marginBottom: "0.5rem" }} />
                                <MenuItem key="switchMerchant" onClick={handleOpenSwiftMerchantModal}>
                                    <ListItemIcon>
                                        <Place className={classes.greyIcon} fontSize="small" />
                                    </ListItemIcon>
                                    <LocalizedStrict id="avatar-menu-menu-item-swift-merchant">
                                        <Typography display="inline" textAlign="center">
                                            Swift merchant
                                        </Typography>
                                    </LocalizedStrict>
                                </MenuItem>
                            </>
                        )}
                    </MatchEnvironment>

                    <Divider sx={{ marginX: "1rem", marginBottom: "0.5rem" }} />
                    <MenuItem key="profile" onClick={navigateToProfilePage}>
                        <ListItemIcon>
                            <Person className={classes.greyIcon} fontSize="small" />
                        </ListItemIcon>
                        <LocalizedStrict id="avatar-menu-menu-item-profile">
                            <Typography display="inline" textAlign="center">
                                User account
                            </Typography>
                        </LocalizedStrict>
                    </MenuItem>
                    <HideForCurrentUserType userTypes={[UserType.USER, UserType.SERVICE_CENTER]}>
                        <MenuItem key="settings" onClick={navigateToSettingsPage}>
                            <ListItemIcon>
                                <Settings className={classes.greyIcon} fontSize="small" />
                            </ListItemIcon>
                            <LocalizedStrict id="avatar-menu-menu-item-settings">
                                <Typography textAlign="center">Settings</Typography>
                            </LocalizedStrict>
                        </MenuItem>
                    </HideForCurrentUserType>
                    <Divider sx={{ marginX: "1rem", marginBottom: "0.5rem" }} />
                    <MenuItem key="logout" onClick={handleLogout}>
                        <ListItemIcon>
                            <Logout className={classes.logoutIcon} fontSize="small" />
                        </ListItemIcon>
                        <LocalizedStrict id="avatar-menu-menu-item-logout">
                            <Typography textAlign="center">Logout</Typography>
                        </LocalizedStrict>
                    </MenuItem>
                </Box>
            </Menu>
            {showSwiftMerchantModal && (
                <SwitchMerchantDialog open={showSwiftMerchantModal} onClose={() => setShowSwiftMerchantModal(false)} />
            )}
            {showChangeUserAccountModal && (
                <ChangeUserAccountDialog
                    open={showChangeUserAccountModal}
                    onClose={() => setShowChangeUserAccountModal(false)}
                />
            )}
            {showHelpDialog && <HelpDialog dialogState={showHelpDialog} setDialogState={setShowHelpDialog} />}
        </>
    )
}
